export const workOrderStatusTone: Record<
  string,
  "positive" | "neutral" | "warning" | "informative"
> = {
  OPEN: "informative",
  IN_PROGRESS: "positive",
  ON_HOLD: "warning",
  COMPLETED: "neutral",
  CANCELED: "neutral",
  DELETED: "neutral",
};

export const workOrderListStatusTone: Record<
  string,
  "positive" | "neutral" | "warning" | "informative"
> = {
  OPEN: "neutral",
  IN_PROGRESS: "informative",
  ON_HOLD: "warning",
  COMPLETED: "positive",
  CANCELED: "neutral",
  DELETED: "neutral",
};

export const workOrderPriorityTone: Record<
  string,
  "negative" | "warning" | "neutral"
> = {
  HIGH: "negative",
  MEDIUM: "warning",
  LOW: "neutral",
  NONE: "neutral",
};

/**
 * Statuses a user may not pick from the edit form's dropdown. Each has its own
 * flow: completing captures notes through the Complete modal, canceling
 * captures a reason, and deleting is a flag-delete rather than a transition.
 */
const NON_SELECTABLE_STATUS_CODES = ["COMPLETED", "CANCELED", "DELETED"];

/**
 * The current status is always offered, so the dropdown can render the value it
 * already holds instead of appearing empty.
 */
export function isSelectableStatusOption(
  code: string,
  currentCode: string,
): boolean {
  return code === currentCode || !NON_SELECTABLE_STATUS_CODES.includes(code);
}

const NON_CANCELABLE_STATUS_CODES = ["CANCELED", "COMPLETED", "DELETED"];

/**
 * Cancel needs the server-granted action and a status that can still be
 * canceled. Shared so the full page and the split view offer it identically.
 */
export function canCancelWorkOrder(workOrder: {
  actions: { CANCEL?: boolean };
  status: { code: string };
}): boolean {
  return (
    Boolean(workOrder.actions.CANCEL) &&
    !NON_CANCELABLE_STATUS_CODES.includes(workOrder.status.code)
  );
}

/**
 * Delete needs the server-granted action and a Work Order that is not already
 * deleted. Shared so the full page and the split view offer it identically.
 */
export function canDeleteWorkOrder(workOrder: {
  actions: { DELETE?: boolean };
  status: { code: string };
}): boolean {
  return (
    Boolean(workOrder.actions.DELETE) && workOrder.status.code !== "DELETED"
  );
}

export function displayWorkOrderNumber(number: string) {
  return number.toUpperCase().startsWith("WO-") ? number : `WO-${number}`;
}

/**
 * The list card shows the bare number with a single "#" ("#123"); the API may
 * or may not return it already prefixed with "WO-".
 */
export function formatWorkOrderCardNumber(number: string) {
  return `#${number.replace(/^WO-/i, "")}`;
}
